import { useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { mockLeads } from '@/mocks/leads';
import { mockPayoutLines } from '@/mocks/payouts';
import { mockVerticals } from '@/mocks/verticals';
import { LayoutGrid, BarChart3, Globe } from 'lucide-react';

export default function SharedReportPage() {
  const { token } = useParams<{ token: string }>();
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');

  const data = useMemo(() => {
    return mockVerticals.map(v => {
      const vLeads = mockLeads.filter(l => l.vertical_id === v.id && !l.deleted_at);
      const payouts = mockPayoutLines.filter(p => {
        const lead = mockLeads.find(l => l.id === p.lead_id);
        return lead?.vertical_id === v.id;
      });
      return {
        dimension: v.name,
        'Leads received': vLeads.length,
        'Leads routed': vLeads.filter(l => l.routing_status === 'assigned').length,
        'Gross payout EUR': payouts.reduce((s, p) => s + p.amount_eur, 0),
        'Avg quality score': vLeads.length > 0
          ? vLeads.reduce((s, l) => s + (l.quality_score || 0), 0) / vLeads.length
          : 0,
      };
    });
  }, []);

  return (
    <div className="min-h-[60vh] py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
            <Globe size={20} style={{ color: 'var(--text-muted)' }} />
          </div>
          <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Shared Report</h1>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>PII redacted · Partner data suppressed</p>
        </div>

        <div className="rounded-xl p-3 text-center" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>This is a shared report. No personally identifiable information is displayed.</p>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setViewMode('table')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all duration-200"
            style={
              viewMode === 'table'
                ? { background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }
                : { background: 'var(--bg-input)', color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }
            }
          >
            <LayoutGrid size={12} />
            Table
          </button>
          <button
            onClick={() => setViewMode('chart')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all duration-200"
            style={
              viewMode === 'chart'
                ? { background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }
                : { background: 'var(--bg-input)', color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }
            }
          >
            <BarChart3 size={12} />
            Chart
          </button>
        </div>

        {viewMode === 'table' ? (
          <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}>
                  <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Vertical</th>
                  <th className="text-right px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Leads</th>
                  <th className="text-right px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Routed</th>
                  <th className="text-right px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Payout EUR</th>
                  <th className="text-right px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Avg Quality</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
                {data.map((row, i) => (
                  <tr key={i} className="transition-colors hover-surface">
                    <td className="px-4 py-3 font-medium" style={{ color: 'var(--text-secondary)' }}>{row.dimension}</td>
                    <td className="px-4 py-3 text-right" style={{ color: 'var(--text-tertiary)' }}>{row['Leads received']}</td>
                    <td className="px-4 py-3 text-right" style={{ color: 'var(--text-tertiary)' }}>{row['Leads routed']}</td>
                    <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>€{row['Gross payout EUR'].toLocaleString()}</td>
                    <td className="px-4 py-3 text-right" style={{ color: 'var(--text-tertiary)' }}>{row['Avg quality score'].toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-xl p-5" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
            <SimpleBarChart data={data} />
          </div>
        )}
      </div>
    </div>
  );
}

function SimpleBarChart({ data }: { data: any[] }) {
  const maxLeads = Math.max(...data.map(d => d['Leads received'] || 0), 1);
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Leads Received</p>
        <div className="space-y-2.5">
          {data.map((row, i) => {
            const val = row['Leads received'] || 0;
            const pct = (val / maxLeads) * 100;
            return (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs w-28 truncate" style={{ color: 'var(--text-muted)' }}>{row.dimension}</span>
                <div className="flex-1 h-5 rounded-lg overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                  <div className="h-full rounded-lg transition-all" style={{ width: `${Math.max(pct, 2)}%`, background: 'var(--text-muted)' }} />
                </div>
                <span className="text-xs w-12 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>{val}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
